import React, { type ReactNode } from 'react';
import type { DefaultSizes } from '../../types/Sizes';
import { cn } from '../../utils/classes';
import { AvatarSizeClasses } from '../AvatarSizeClasses';

export interface AvatarProps {
  /**
   * @param icon - icon to display on the avatar
   */
  icon?: ReactNode;
  /**
   * @param imgURL - image to display on the avatar
   */
  imgURL?: string | null;
  /**
   * @param size - size of the component
   */
  size?: DefaultSizes;
  /**
   * @param text - text to display in the absence of an image
   */
  text: string;
  /**
   * @param variant - image style variant to be displayed
   */
  variant?: 'circle' | 'rounded';
  /**
   * @param highlighted - show ring around the avatar
   */
  highlighted?: boolean;
  /**
   * Additional className for custom styling
   */
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  icon,
  imgURL,
  text,
  size = 'md',
  variant = 'circle',
  highlighted = false,
  className,
}) => {
  const { root, fontSize, iconSize } = AvatarSizeClasses({ size });

  const displayImage = !!imgURL;
  const displayIcon = !imgURL && !!icon;
  const displayFallback = !imgURL && !icon;

  return (
    <div
      className={cn('flex justify-center overflow-hidden text-center bg-gray-200', root, className, {
        rounded: variant === 'rounded',
        'rounded-full': variant === 'circle',
        'ring-2 ring-white': highlighted,
      })}
    >
      {displayImage && <img className="inline-block h-full w-full object-cover" src={imgURL} alt={text} />}
      {displayIcon && <div className={cn('inline-flex justify-center items-center self-center', iconSize)}>{icon}</div>}
      {displayFallback && (
        <span
          className={cn('inline-flex items-center justify-center font-medium leading-none text-gray-500', fontSize)}
        >
          {text.slice(0, 3).toUpperCase()}
        </span>
      )}
    </div>
  );
};
